import React, { useEffect, useState } from 'react';
import { DeviceMobile, X, Lightning, Share, Plus } from '@phosphor-icons/react';
import { Button } from './ui/button';

// InstallPrompt — first-run bottom-sheet inviting users to install Stockaris
// as a PWA. Handles two platform flows:
//
//   • Android/Chromium: captures `beforeinstallprompt`, shows an "Install"
//     button that triggers the native prompt.
//   • iOS Safari: Apple refuses to expose beforeinstallprompt, so we render
//     a tiny visual guide to Share → "Add to Home Screen".
//
// The banner auto-hides when:
//   • the app is already running standalone
//   • the user dismissed it in the last 7 days
//   • the browser can't install a PWA at all (no event + not iOS Safari)
const SNOOZE_KEY = 'stockaris.pwa_install.snoozed_until';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isIOS() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isTouch = 'ontouchend' in document;
  // iPadOS >=13 reports as Mac; fall back to touch detection
  return (/iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && isTouch));
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null); // 'chromium' | 'ios' | null

  useEffect(() => {
    if (isStandalone()) return;
    const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < snoozed) return;

    // Chromium / Edge / Brave: wait for the beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('chromium');
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setDeferredPrompt(null);
      setVisible(false);
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + 10 * SNOOZE_MS));
    };
    window.addEventListener('appinstalled', installedHandler);

    // iOS Safari never fires beforeinstallprompt — show a manual guide after
    // a short delay so it doesn't flash on first paint.
    let iosTimer;
    if (isIOS() && !isStandalone()) {
      iosTimer = setTimeout(() => {
        setPlatform('ios');
        setVisible(true);
      }, 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  if (!visible) return null;

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setVisible(false);
  };

  const doInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + 10 * SNOOZE_MS));
      }
      setDeferredPrompt(null);
      setVisible(false);
    } catch (_) {
      // user cancelled / browser blocked — treat as snooze
      snooze();
    }
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="pwa-install-prompt"
      className="fixed z-[80] left-3 right-3 bottom-[5.5rem] md:left-auto md:right-6 md:bottom-6 md:w-96 bg-card border-2 border-foreground shadow-[8px_8px_0_0_rgba(0,0,0,0.15)]"
    >
      <button
        type="button"
        onClick={snooze}
        data-testid="pwa-install-dismiss"
        className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X size={16} weight="bold" />
      </button>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 bg-brand flex items-center justify-center">
            <DeviceMobile size={16} weight="fill" className="text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand">
            Install · faster than Safari
          </span>
        </div>
        <h3 className="text-lg font-bold">Add Stockaris to your home screen</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Launches instantly, fills the screen (no browser bar), and keeps your last-viewed pick lists ready even on weak Wi-Fi.
        </p>

        {platform === 'chromium' && (
          <div className="mt-4 flex gap-2">
            <Button
              onClick={doInstall}
              data-testid="pwa-install-accept"
              className="flex-1 bg-brand hover:bg-brand/90 text-white rounded-none font-bold uppercase tracking-widest text-xs h-10"
            >
              <Lightning size={14} weight="fill" className="mr-1" />
              Install
            </Button>
            <Button
              onClick={snooze}
              variant="outline"
              data-testid="pwa-install-snooze"
              className="rounded-none font-bold uppercase tracking-widest text-xs h-10"
            >
              Not now
            </Button>
          </div>
        )}

        {platform === 'ios' && (
          <div className="mt-4">
            <ol className="space-y-2 text-xs text-foreground">
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 bg-foreground text-background text-[10px] font-bold flex items-center justify-center mt-0.5">1</span>
                <span>
                  Tap the <Share size={12} weight="bold" className="inline mx-0.5" /> <b>Share</b> button in Safari's toolbar.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 bg-foreground text-background text-[10px] font-bold flex items-center justify-center mt-0.5">2</span>
                <span>
                  Scroll and tap <Plus size={12} weight="bold" className="inline mx-0.5" /> <b>Add to Home Screen</b>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 bg-foreground text-background text-[10px] font-bold flex items-center justify-center mt-0.5">3</span>
                <span>Tap <b>Add</b> — done.</span>
              </li>
            </ol>
            <Button
              onClick={snooze}
              variant="outline"
              data-testid="pwa-install-snooze"
              className="mt-3 w-full rounded-none font-bold uppercase tracking-widest text-xs h-10"
            >
              Got it
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
